import { getDisplayLabelForFieldType, getNestedOrFloatingElementById } from '@fs/zion-sls'
import { namePartTypeKeys, personDetailTypeKeys } from './helpers'

/**
 * Retrieves an array of highlight IDs that match the specified field ID.
 *
 * @param {Array} highlights - The list of highlight objects to search through. Usually obtained from h.getHighlights().mainEditHighlights.
 * @param {string} fieldId - The field ID to filter highlights by.
 * @returns {string[]} An array of highlight IDs that have the specified field ID. Returns an empty array if no matches are found or if inputs are invalid.
 */
export const getHighlightIdsByFieldId = (highlights, fieldId) => {
  if (!highlights || !fieldId) return []
  const filteredHighlights = highlights.filter((highlight) => highlight.fieldId === fieldId) || []

  return filteredHighlights.map((highlight) => highlight.id)
}

/**
 * Extracts unique field IDs from the provided form values object.
 *
 * Iterates over personal detail and name part to collect all IDs present
 * in the form values and returns a deduplicated array of these IDs.
 *
 * This is useful for determining which fields are currently represented in the form, and show highlights accordingly.
 *
 * @param {Object} formValues - The form values object containing personal details and names. Usually obtained from formMethods.getValues().
 * @returns {string[]} An array of unique IDs extracted from the form values.
 */
export const getFieldIdsFromFormValues = (formValues) => {
  const { names } = formValues || {}

  const namePartIds = []
  const personalDetailsIds = []

  personDetailTypeKeys.forEach((partType) => {
    const part = formValues?.[partType]

    if (part?.id) personalDetailsIds.push(part.id)
  })

  names?.forEach((name) => {
    namePartTypeKeys.forEach((partType) => {
      const part = name?.[partType]

      // Use field id for highlighting since highlights are associated with field elements
      if (part?.id) namePartIds.push(part.id)
    })
  })

  return [...new Set([...namePartIds, ...personalDetailsIds])]
}

// Prepare field object for HighlightButtonOld component
export function prepareFieldForHighlights(getValues, fieldType, fieldFormName, slsTypes, images) {
  const currentFieldFormValue = getValues(fieldFormName)
  const elementFromImages = getNestedOrFloatingElementById(images, currentFieldFormValue?.id)
  const generatedDisplayLabel = getDisplayLabelForFieldType(fieldType, slsTypes)

  return {
    ...currentFieldFormValue,
    ...elementFromImages,
    elementType: currentFieldFormValue?.elementType || elementFromImages?.elementType || 'FIELD',
    // Ensure displayLabel is set, preferring element's displayLabel, then form value's displayLabel, then generated label, then fieldType as fallback
    displayLabel:
      elementFromImages?.displayLabel || currentFieldFormValue?.displayLabel || generatedDisplayLabel || fieldType,
    name: fieldFormName,
    value: (currentFieldFormValue?.formValue ?? currentFieldFormValue?.value) || '',
    fieldType,
  }
}

// Handlers to manage highlight hover on focus/blur of input fields
export function handleInputFocus(fieldIdPath, getValues, h) {
  const fieldId = getValues(fieldIdPath)
  if (fieldId) {
    h.hoverHighlights({ elementIds: [fieldId] })
  }
}

export function handleInputBlur(h) {
  h.unHover()
}

export function updateFormAfterSharedFieldLink(fieldArrayMethods, onSaveData) {
  if (!onSaveData?.dispatches || !onSaveData?.activeFieldInfo?.id) return
  const updateIndex = fieldArrayMethods.fields.findIndex((f) => f.id === onSaveData.activeFieldInfo.id)
  const createFieldDispatch = onSaveData.dispatches.find((disp) => disp.type === 'CREATE_ELEMENT')
  fieldArrayMethods.update(updateIndex, {
    ...fieldArrayMethods.fields[updateIndex],
    id: createFieldDispatch.value.id,
    value: onSaveData.newValue,
  })
}
